import {useMemo} from 'react';
import {useQueries} from 'react-query';

import {useAuth} from 'app/modules/auth';
import {getFeatureFlag} from 'app/requests';

const NO_FLAGS: Record<string, boolean> = {};

/**
 * As flags de produto dos itens do menu (`flag` em `appMenu.ts`), por usuário,
 * pelo config-service. Erro e leitura em curso contam como desligada: o item
 * só aparece com a flag ligada. A chave de cada uma é a do `useOpsNextFlag`
 * (`['ops-next-flag', flag, uid]`, com o mesmo valor, um booleano), então a
 * página que confere a mesma flag não pergunta de novo. O layout não importa
 * de `app/ops-next` (regras 5 e 8 de `architecture.ts`), por isso a leitura
 * mora aqui.
 */
export const useMenuFlags = (
  flags: readonly string[]
): Record<string, boolean> => {
  const {currentUser} = useAuth();
  const uid = currentUser?.uid || '';
  const email = currentUser?.user?.email;

  const results = useQueries(
    flags.map((flag) => ({
      queryKey: ['ops-next-flag', flag, uid],
      queryFn: async () => {
        try {
          const response = await getFeatureFlag(flag, uid, email);
          return Boolean(response?.flag?.value);
        } catch (error) {
          return false;
        }
      },
      enabled: Boolean(uid),
      staleTime: 5 * 60 * 1000,
    }))
  );

  // Um objeto novo só quando alguma flag muda: o menu se refaz por ele
  const on = results.map(({data}) => data === true);
  const key = on.join();
  return useMemo(
    () =>
      on.some(Boolean)
        ? flags.reduce<Record<string, boolean>>(
            (read, flag, index) => ({...read, [flag]: on[index]}),
            {}
          )
        : NO_FLAGS,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flags, key]
  );
};
